import { useEffect, useState, useCallback } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  LayoutDashboard,
  CreditCard,
  Users,
  Receipt,
  FileText,
  Package,
  HardHat,
  Car,
  Settings,
  Plus,
  Upload,
  Search,
  Sparkles,
  DollarSign,
  TrendingUp,
  Calculator,
  Send,
  Clock,
  BookOpen,
} from 'lucide-react';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ElementType;
  action: () => void;
  keywords?: string[];
  category: 'navigation' | 'action' | 'ai';
}

export default function CommandBar() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  // Toggle with ⌘K
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const runCommand = useCallback((command: () => void) => {
    setOpen(false);
    setSearch('');
    command();
  }, []);

  const commands: CommandItem[] = [
    // Navigation
    {
      id: 'dashboard',
      label: 'Go to Dashboard',
      icon: LayoutDashboard,
      action: () => navigate('/'),
      category: 'navigation',
      keywords: ['home', 'overview'],
    },
    {
      id: 'invoices',
      label: 'Go to Invoices',
      icon: FileText,
      action: () => navigate('/invoicing/invoices'),
      category: 'navigation',
      keywords: ['bills', 'billing'],
    },
    {
      id: 'transactions',
      label: 'Go to Transactions',
      icon: Receipt,
      action: () => navigate('/taxes/transactions'),
      category: 'navigation',
      keywords: ['expenses', 'spending'],
    },
    {
      id: 'employees',
      label: 'Go to Employees',
      icon: Users,
      action: () => navigate('/payroll/employees'),
      category: 'navigation',
      keywords: ['payroll', 'team', 'staff'],
    },
    {
      id: 'debts',
      label: 'Go to Debt Accounts',
      icon: CreditCard,
      action: () => navigate('/debts'),
      category: 'navigation',
      keywords: ['loans', 'credit'],
    },
    {
      id: 'inventory',
      label: 'Go to Inventory',
      icon: Package,
      action: () => navigate('/inventory/products'),
      category: 'navigation',
      keywords: ['products', 'stock'],
    },
    {
      id: 'contractors',
      label: 'Go to Contractors',
      icon: HardHat,
      action: () => navigate('/contractors'),
      category: 'navigation',
      keywords: ['1099', 'freelance'],
    },
    {
      id: 'mileage',
      label: 'Go to Mileage',
      icon: Car,
      action: () => navigate('/mileage/log'),
      category: 'navigation',
      keywords: ['driving', 'trips'],
    },
    {
      id: 'reports',
      label: 'Go to Reports',
      icon: TrendingUp,
      action: () => navigate('/reports'),
      category: 'navigation',
      keywords: ['analytics', 'pnl'],
    },
    {
      id: 'accounting',
      label: 'Go to Chart of Accounts',
      icon: BookOpen,
      action: () => navigate('/accounting/chart-of-accounts'),
      category: 'navigation',
      keywords: ['journal', 'ledger'],
    },
    {
      id: 'settings',
      label: 'Go to Settings',
      icon: Settings,
      action: () => navigate('/settings'),
      category: 'navigation',
      keywords: ['preferences', 'config'],
    },

    // Actions
    {
      id: 'new-invoice',
      label: 'Create New Invoice',
      description: 'Bill a client',
      icon: Plus,
      action: () => navigate('/invoicing/invoices/new'),
      category: 'action',
      keywords: ['bill', 'charge'],
    },
    {
      id: 'upload-csv',
      label: 'Upload Bank Statement',
      description: 'Import transactions',
      icon: Upload,
      action: () => navigate('/taxes/upload'),
      category: 'action',
      keywords: ['import', 'transactions'],
    },
    {
      id: 'log-time',
      label: 'Log Time Entry',
      description: 'Track work hours',
      icon: Clock,
      action: () => navigate('/payroll/timesheet'),
      category: 'action',
      keywords: ['hours', 'timesheet'],
    },
    {
      id: 'add-expense',
      label: 'Add Expense',
      description: 'Record a purchase',
      icon: DollarSign,
      action: () => navigate('/taxes/transactions'),
      category: 'action',
      keywords: ['spend', 'purchase', 'buy'],
    },
    {
      id: 'send-invoice',
      label: 'Send Invoice',
      description: 'Email to client',
      icon: Send,
      action: () => navigate('/invoicing/invoices'),
      category: 'action',
      keywords: ['email', 'bill'],
    },

    // AI Commands (placeholders for now)
    {
      id: 'ai-categorize',
      label: 'Auto-categorize transactions',
      description: 'Let AI sort your expenses',
      icon: Sparkles,
      action: () => toast('AI categorization coming soon!', { icon: '✨' }),
      category: 'ai',
      keywords: ['smart', 'automatic'],
    },
    {
      id: 'ai-tax',
      label: 'Calculate tax estimate',
      description: 'See what you owe',
      icon: Calculator,
      action: () => navigate('/taxes/summary'),
      category: 'ai',
      keywords: ['irs', 'taxes', 'owed'],
    },
  ];

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm z-50"
            onClick={() => setOpen(false)}
          />

          {/* Command Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="fixed top-[20%] left-1/2 -translate-x-1/2 w-full max-w-xl z-50"
          >
            <Command
              className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden"
              loop
            >
              {/* Search Input */}
              <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-200 dark:border-slate-800/50">
                <Search className="w-5 h-5 text-slate-400 dark:text-slate-500" />
                <Command.Input
                  value={search}
                  onValueChange={setSearch}
                  placeholder="Type a command or search..."
                  className="flex-1 bg-transparent text-slate-900 dark:text-white text-lg placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none"
                  autoFocus
                />
                <kbd className="px-2 py-1 text-[10px] font-medium text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-800 rounded-md">
                  ESC
                </kbd>
              </div>

              {/* Results */}
              <Command.List className="max-h-[60vh] overflow-y-auto p-2">
                <Command.Empty className="px-4 py-8 text-center text-slate-500">
                  No results found. Try a different search.
                </Command.Empty>

                {/* AI Commands */}
                <Command.Group heading="AI Assistant" className="px-2 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-violet-600 dark:text-violet-500/70 font-semibold mb-2 px-2">
                    ✨ AI-Powered
                  </p>
                  {commands
                    .filter((c) => c.category === 'ai')
                    .map((cmd) => (
                      <Command.Item
                        key={cmd.id}
                        value={`${cmd.label} ${cmd.keywords?.join(' ')}`}
                        onSelect={() => runCommand(cmd.action)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer text-slate-700 dark:text-slate-300 data-[selected=true]:bg-violet-500/10 data-[selected=true]:text-violet-700 dark:data-[selected=true]:text-violet-300 transition-colors"
                      >
                        <div className="p-2 rounded-lg bg-violet-500/10">
                          <cmd.icon className="w-4 h-4 text-violet-500 dark:text-violet-400" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{cmd.label}</p>
                          {cmd.description && <p className="text-xs text-slate-500">{cmd.description}</p>}
                        </div>
                      </Command.Item>
                    ))}
                </Command.Group>

                {/* Quick Actions */}
                <Command.Group heading="Quick Actions" className="px-2 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 px-2">Actions</p>
                  {commands
                    .filter((c) => c.category === 'action')
                    .map((cmd) => (
                      <Command.Item
                        key={cmd.id}
                        value={`${cmd.label} ${cmd.keywords?.join(' ')}`}
                        onSelect={() => runCommand(cmd.action)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer text-slate-700 dark:text-slate-300 data-[selected=true]:bg-slate-100 dark:data-[selected=true]:bg-slate-800 data-[selected=true]:text-slate-900 dark:data-[selected=true]:text-white transition-colors"
                      >
                        <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800">
                          <cmd.icon className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{cmd.label}</p>
                          {cmd.description && <p className="text-xs text-slate-500">{cmd.description}</p>}
                        </div>
                      </Command.Item>
                    ))}
                </Command.Group>

                {/* Navigation */}
                <Command.Group heading="Navigation" className="px-2 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 px-2">Go To</p>
                  {commands
                    .filter((c) => c.category === 'navigation')
                    .map((cmd) => (
                      <Command.Item
                        key={cmd.id}
                        value={`${cmd.label} ${cmd.keywords?.join(' ')}`}
                        onSelect={() => runCommand(cmd.action)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer text-slate-500 dark:text-slate-400 data-[selected=true]:bg-slate-100 dark:data-[selected=true]:bg-slate-800 data-[selected=true]:text-slate-900 dark:data-[selected=true]:text-white transition-colors"
                      >
                        <cmd.icon className="w-4 h-4" />
                        <span>{cmd.label}</span>
                      </Command.Item>
                    ))}
                </Command.Group>
              </Command.List>

              {/* Footer */}
              <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800/50 flex items-center justify-between text-[11px] text-slate-500">
                <div className="flex items-center gap-4">
                  <span>
                    <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[10px]">↑↓</kbd> navigate
                  </span>
                  <span>
                    <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[10px]">↵</kbd> select
                  </span>
                </div>
                <span className="text-violet-600 dark:text-violet-500/70">⌘K to toggle</span>
              </div>
            </Command>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Trigger button for mobile / visual affordance
export function CommandTrigger() {
  return (
    <button
      onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
      className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700/50 rounded-xl text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors"
    >
      <Search className="w-4 h-4" />
      <span className="hidden sm:inline">Search or command...</span>
      <kbd className="hidden sm:inline px-1.5 py-0.5 bg-white dark:bg-slate-900 rounded text-[10px] text-slate-400 dark:text-slate-500 ml-2">
        ⌘K
      </kbd>
    </button>
  );
}
